import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import * as orgApi from '../api/organizations';
import * as groupsApi from '../api/groups';
import { useOrg } from '../lib/org';
import { useToast } from '../components/toast';
import Tabs from '../components/Tabs';
import ListingPill from '../components/ListingPill';
import VendorLogo from '../components/VendorLogo';
import MarketplaceCategoriesPanel from '../components/MarketplaceCategoriesPanel';
import OrgLeadsPanel from '../components/OrgLeadsPanel';
import OrgFeesPanel from '../components/OrgFeesPanel';
import { Spinner, ErrorAlert, EmptyState, TextareaField, formatDate } from '../components/ui';
import {
  LISTING_ACTION_LABELS,
  LISTING_ACTIONS,
  LISTING_STATUS_LABELS,
  REVIEW_POLICY_LABELS,
  approvalBlockers,
} from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

const FILTERS = ['pending', 'approved', 'suspended', 'invited', 'declined', 'removed', 'withdrawn'];

function ListingReview({ listing, onChanged }) {
  const toast = useToast();
  const [checks, setChecks] = useState(() =>
    Object.fromEntries(
      Object.entries(listing.requirementChecks || {}).map(([k, v]) => [k, Boolean(v?.verified)])
    )
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);
  const [blockers, setBlockers] = useState([]);
  const { review } = listing;
  const local = approvalBlockers(review, {
    checks: Object.fromEntries(Object.entries(checks).map(([k, v]) => [k, { verified: v }])),
    note,
  });

  async function act(action) {
    if (LISTING_ACTIONS[action]?.noteRequired && !note.trim()) {
      setBlockers(['Add a note; the vendor sees it']);
      return;
    }
    setBusy(action);
    setBlockers([]);
    try {
      const data = await marketApi.actOnListing(
        listing.listingId,
        action,
        note.trim() || undefined,
        action === 'approve' ? checks : undefined
      );
      toast.success(
        `${listing.vendorName}: ${LISTING_STATUS_LABELS[data.listing.status].toLowerCase()}`
      );
      setNote('');
      onChanged(data.listing);
    } catch (err) {
      setBlockers(err?.details?.blockers || []);
      toast.error(err.message || 'Nothing changed');
    } finally {
      setBusy(null);
    }
  }

  const vendor = listing.vendor;
  return (
    <article className={cn(ui.card, 'mb-4')} aria-label={listing.vendorName}>
      <div className={cn(ui.row, 'mb-2')}>
        {vendor && <VendorLogo vendor={vendor} size="size-10" />}
        <h3 className="m-0 text-title-s">
          <Link to={`/marketplace/vendors/${encodeURIComponent(listing.vendorId)}`}>
            {listing.vendorName}
          </Link>
        </h3>
        <ListingPill status={listing.status} />
      </div>
      <p className={cn(ui.hint, 'mt-0')}>
        {listing.categories
          .map(c => `${c.name} (${REVIEW_POLICY_LABELS[c.reviewPolicy].label})`)
          .join(', ') || 'No categories'}
        {listing.appliedAt ? ` - applied ${formatDate(listing.appliedAt)}` : ''}
        {listing.status === 'invited' && listing.invitedAt
          ? ` - invited ${formatDate(listing.invitedAt)}`
          : ''}
      </p>
      {vendor?.contact && (
        <p className="mt-0 text-body-s">
          {[vendor.contact.email, vendor.contact.phone, vendor.contact.website]
            .filter(Boolean)
            .join(' - ')}
        </p>
      )}
      {listing.statusNote && <p className="text-body-s">Last note: {listing.statusNote}</p>}

      {review.requirements.length > 0 && (
        <fieldset className="mb-3">
          <legend className={ui.label}>
            Requirements{review.requirementsVerified ? ' to validate before approving' : ''}
          </legend>
          {review.requirements.map(r => (
            <label key={r.key} className="flex items-start gap-2 text-body-s">
              <input
                type="checkbox"
                className={cn(ui.checkbox, 'mt-1')}
                checked={Boolean(checks[r.key])}
                disabled={listing.status !== 'pending'}
                onChange={e => setChecks(prev => ({ ...prev, [r.key]: e.target.checked }))}
              />
              <span>
                <strong>{r.label}</strong> ({r.categoryName}):{' '}
                {listing.requirementAnswers?.[r.key] || <em>not given</em>}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {!review.delegated && listing.status === 'pending' && (
        <p className={cn(ui.hint, 'mt-0')}>Only an org admin can decide on this application.</p>
      )}
      {listing.actions.length > 0 && (
        <>
          <TextareaField
            label="Note to the vendor"
            hint={
              review.noteRequired && listing.status === 'pending'
                ? 'Required to approve: say what you checked.'
                : 'Required to decline, suspend or remove.'
            }
            rows={2}
            maxLength={1000}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          {blockers.length > 0 && (
            <ul className={ui.alertError} role="alert">
              {blockers.map(b => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
          <div className={ui.row}>
            {listing.actions.map(action => (
              <button
                key={action}
                type="button"
                className={cn(
                  action === 'approve' || action === 'reinstate' ? ui.btnPrimary : ui.btnSecondary,
                  ui.btnSm
                )}
                disabled={Boolean(busy) || (action === 'approve' && local.length > 0)}
                onClick={() => act(action)}
              >
                {busy === action ? 'Working...' : LISTING_ACTION_LABELS[action]}
              </button>
            ))}
          </div>
        </>
      )}
    </article>
  );
}

function ApplicationsPanel() {
  const [status, setStatus] = useState('pending');
  const [listings, setListings] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setListings(null);
    try {
      const data = await marketApi.listOrgListings(status);
      setListings(data.listings);
    } catch (err) {
      setError(err);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section>
      <div className={cn(ui.row, 'mb-4')} role="group" aria-label="Show listings that are">
        {FILTERS.map(s => (
          <button
            key={s}
            type="button"
            className={cn(status === s ? ui.btnPrimary : ui.btnSecondary, ui.btnSm)}
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
          >
            {LISTING_STATUS_LABELS[s]}
          </button>
        ))}
      </div>
      {error ? (
        <ErrorAlert error={error} />
      ) : listings === null ? (
        <Spinner label="Loading listings..." />
      ) : listings.length === 0 ? (
        <EmptyState message={`No listings are ${LISTING_STATUS_LABELS[status].toLowerCase()}.`} />
      ) : (
        listings.map(l => (
          <ListingReview
            key={`${l.listingId}-${l.updatedAt}`}
            listing={l}
            onChanged={updated =>
              setListings(prev =>
                prev
                  .filter(x => x.listingId !== updated.listingId || updated.status === status)
                  .map(x => (x.listingId === updated.listingId ? updated : x))
              )
            }
          />
        ))
      )}
    </section>
  );
}

function InvitePanel({ orgName }) {
  const toast = useToast();
  const [vendors, setVendors] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setVendors((await marketApi.listDiscoverableVendors()).vendors);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function invite(v) {
    setBusy(v.vendorId);
    try {
      const data = await marketApi.inviteVendor(v.vendorId);
      toast.success(
        data.ownerIsMember
          ? `${v.businessName} was invited`
          : `${v.businessName} was invited; its owner joins ${orgName} before accepting. Invite them as a member too.`
      );
      load();
    } catch (err) {
      toast.error(err.message || 'The invitation was not sent');
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!vendors) return <Spinner label="Loading vendors..." />;
  if (!vendors.length)
    return <EmptyState message="No vendor has made its platform profile discoverable yet." />;
  return (
    <ul className="m-0 list-none p-0">
      {vendors.map(v => (
        <li key={v.vendorId} className={cn(ui.row, ui.spread, 'border-b border-z-line py-3')}>
          <span className={ui.row}>
            <VendorLogo vendor={v} size="size-10" />
            <span>
              <strong>{v.businessName}</strong>
              {v.serviceArea && <span className={cn(ui.hint, 'block')}>{v.serviceArea}</span>}
            </span>
          </span>
          {v.listingStatus && !['declined', 'withdrawn', 'removed'].includes(v.listingStatus) ? (
            <ListingPill status={v.listingStatus} />
          ) : (
            <button
              type="button"
              className={cn(ui.btnSecondary, ui.btnSm)}
              disabled={busy === v.vendorId}
              onClick={() => invite(v)}
              aria-label={`Invite ${v.businessName}`}
            >
              Invite
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function ApproversPanel() {
  const { activeOrg, refresh } = useOrg();
  const toast = useToast();
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(
    () => new Set(activeOrg?.marketplaceApproverGroupIds || [])
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    groupsApi
      .listGroups({ limit: 100 })
      .then(d => setGroups((d.groups || []).filter(g => !g.orgId || g.orgId === activeOrg?.orgId)))
      .catch(() => setGroups([]));
  }, [activeOrg?.orgId]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await orgApi.updateOrganization(activeOrg.orgId, {
        settings: { marketplaceApproverGroupIds: [...selected] },
      });
      toast.success('Marketplace approvers saved');
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err.message || 'Not saved');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className={ui.card}>
      <p className={cn(ui.muted, 'mt-0 text-body-s')}>
        Org admins always decide on vendor applications. Members of the groups you tick may approve
        and decline them too, except in restricted and licensed categories.
      </p>
      {groups === null ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <EmptyState message="This organization has no groups yet." />
      ) : (
        <fieldset>
          <legend className={ui.label}>Groups that approve vendors</legend>
          {groups.map(g => (
            <label key={g.groupId} className={cn(ui.row, 'text-body-s')}>
              <input
                type="checkbox"
                className={ui.checkbox}
                checked={selected.has(g.groupId)}
                onChange={e =>
                  setSelected(prev => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(g.groupId);
                    else next.delete(g.groupId);
                    return next;
                  })
                }
              />
              {g.name}
            </label>
          ))}
        </fieldset>
      )}
      <button type="submit" className={cn(ui.btnPrimary, 'mt-4')} disabled={saving || !groups}>
        {saving ? 'Saving...' : 'Save approvers'}
      </button>
    </form>
  );
}

/**
 * /org/marketplace: running the organization's marketplace
 * (docs/Decisions.md D68): applications, categories and fees, invitations
 * and delegated approvers. Approvers who are not org admins see the
 * applications only.
 */
export default function OrgMarketplace() {
  const { activeOrg } = useOrg();
  const isOrgAdmin = activeOrg?.role === 'org-admin';
  const tabs = [
    { id: 'applications', label: 'Applications' },
    ...(isOrgAdmin
      ? [
          { id: 'leads', label: 'Leads' },
          { id: 'fees', label: 'Fees and payouts' },
          { id: 'categories', label: 'Categories and fees' },
          { id: 'invite', label: 'Invite vendors' },
          { id: 'approvers', label: 'Approvers' },
        ]
      : []),
  ];
  const [tab, setTab] = useState('applications');

  if (!activeOrg) {
    return (
      <>
        <h1 className={ui.h1}>Marketplace</h1>
        <EmptyState message="Choose an organization first." />
      </>
    );
  }
  const investment = activeOrg.orgType === 'investment';
  return (
    <>
      <h1 className={ui.h1}>Manage the marketplace</h1>
      <p className={cn(ui.muted, 'mb-4')}>
        Vendors join {activeOrg.name}, then apply to its marketplace. You approve them, set the fees
        on deals sourced here and keep the directory trustworthy.
        {investment &&
          ' As an investment organization, you use platform categories and flat fees only.'}
      </p>
      <Tabs
        tabs={tabs}
        value={tab}
        onChange={setTab}
        idPrefix="org-marketplace"
        aria-label="Marketplace settings"
      />
      <div
        role="tabpanel"
        id={`org-marketplace-panel-${tab}`}
        aria-labelledby={`org-marketplace-${tab}`}
        className="mt-4"
      >
        {tab === 'applications' && <ApplicationsPanel />}
        {tab === 'leads' && <OrgLeadsPanel />}
        {tab === 'fees' && <OrgFeesPanel />}
        {tab === 'categories' && <MarketplaceCategoriesPanel owner="org" investment={investment} />}
        {tab === 'invite' && <InvitePanel orgName={activeOrg.name} />}
        {tab === 'approvers' && <ApproversPanel />}
      </div>
    </>
  );
}
